import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import Layout from '../components/layout'
import Icon from '../components/icon'
import ChatMockup from '../components/chat-mockup'
import { SITE, WA_SECRETARIO } from '../site'
import './secretario.css'

const HERO_CHAT = [
  { from: 'user', text: '¿Qué tengo mañana?', time: '7:02' },
  {
    from: 'bot',
    text: 'Mañana tienes 2 eventos: 9:00 a. m. comité de ventas y 3:00 p. m. reunión con Ana (con Meet).',
    time: '7:02',
  },
  { from: 'user', text: 'Recuérdame a las 8:30 preparar el informe del comité', time: '7:03' },
  { from: 'bot', text: 'Listo, mañana a las 8:30 a. m. te recuerdo «preparar el informe del comité».', time: '7:03' },
]

const CAPABILITIES = [
  {
    icon: 'calendar',
    title: 'Tu agenda',
    text: 'Consulta tus eventos y crea, mueve o cancela reuniones, con enlace de Google Meet si lo pides.',
    example: '«Mueve la reunión con Ana para el jueves a las 10»',
  },
  {
    icon: 'mail',
    title: 'Correos',
    text: 'Redacta y envía correos desde tu Gmail a quien le indiques.',
    example: '«Escríbele a Carlos que el informe llega el viernes»',
  },
  {
    icon: 'tasks',
    title: 'Tareas',
    text: 'Crea tareas en Google Tasks, con fecha límite, y márcalas como completadas.',
    example: '«Agrega a mis pendientes renovar el seguro antes del 15»',
  },
  {
    icon: 'bell',
    title: 'Recordatorios y encargos',
    text: 'Programa avisos de una vez o que se repiten, y encargos que Secretario hace por ti a su hora.',
    example: '«Cada lunes a las 7 mándame mi agenda de la semana»',
  },
  {
    icon: 'users',
    title: 'Contactos',
    text: 'Busca el correo o el teléfono de una persona en tus contactos para escribirle o invitarla.',
    example: '«Invita a Laura de finanzas a la reunión»',
  },
  {
    icon: 'file',
    title: 'Documentos',
    text: 'Crea documentos de Google Docs con el contenido que le dictes y los comparte con quien digas.',
    example: '«Crea un documento con el acta de hoy y compártelo con el equipo»',
  },
]

const STEPS = [
  {
    title: 'Solicita acceso',
    text: 'Escríbenos por WhatsApp. Durante el acceso anticipado registramos tu número para que Secretario te atienda.',
  },
  {
    title: 'Escríbele como a una persona',
    text: 'Por mensaje o nota de voz, desde tu WhatsApp de siempre. No hay nada que instalar.',
  },
  {
    title: 'Conecta tu cuenta de Google',
    text: 'La primera vez que pidas algo de tu agenda, tu correo o tus tareas, Secretario te envía un enlace seguro para autorizarlo en Google.',
  },
  {
    title: 'Confirma y listo',
    text: 'Antes de enviar, crear, cambiar o compartir algo, te muestra exactamente qué va a hacer y espera tu «sí».',
  },
]

const SCOPES = [
  {
    product: 'Gmail',
    access: 'Enviar correos en tu nombre',
    scope: 'gmail.send',
    use: 'Envía los correos que le pides y confirmas. No puede leer, buscar ni borrar los correos de tu bandeja.',
  },
  {
    product: 'Google Calendar',
    access: 'Ver y editar los eventos de tus calendarios',
    scope: 'calendar.events',
    use: 'Consulta tu agenda y crea, actualiza o cancela los eventos que le pides, con Google Meet si lo indicas.',
  },
  {
    product: 'Google Tasks',
    access: 'Ver y editar tus tareas',
    scope: 'tasks',
    use: 'Consulta tus listas y tareas, crea tareas nuevas y marca como completadas las que le indiques.',
  },
  {
    product: 'Google Contacts',
    access: 'Ver tus contactos y otros contactos (solo lectura)',
    scope: 'contacts.readonly · contacts.other.readonly',
    use: 'Busca el correo o el teléfono de la persona que mencionas. No crea, cambia ni borra contactos.',
  },
  {
    product: 'Google Drive',
    access: 'Solo los archivos que crea Secretario',
    scope: 'drive.file',
    use: 'Crea documentos de Google Docs y los comparte cuando se lo pides. No ve el resto de tu Drive.',
  },
]

const CONTROLS = [
  {
    icon: 'shield',
    title: 'Nada sin tu «sí»',
    text: 'Las consultas las resuelve solo; cualquier acción que cambie algo espera tu confirmación, y vence si no llega a tiempo.',
  },
  {
    icon: 'logout',
    title: 'Desconecta cuando quieras',
    text: 'Escríbele «desconecta mi cuenta de Google»: revocamos el acceso en Google y borramos la autorización. También puedes quitarlo en tu cuenta de Google.',
  },
  {
    icon: 'clock',
    title: 'Memoria corta',
    text: 'Recuerda tus últimos mensajes para entender el contexto y los olvida tras 24 horas sin conversación.',
  },
  {
    icon: 'lock',
    title: 'Datos protegidos',
    text: 'Las autorizaciones se guardan cifradas, todo viaja por conexiones seguras y no usamos tus datos para entrenar modelos de IA.',
  },
]

const FAQ = [
  {
    q: '¿Tengo que instalar algo?',
    a: 'No. Secretario funciona dentro de WhatsApp: le escribes al número de Secretario como a cualquier contacto.',
  },
  {
    q: '¿Secretario puede leer mis correos?',
    a: 'No. El permiso de Gmail que pide solo permite enviar correos. No puede leer, buscar ni borrar los mensajes de tu bandeja de entrada.',
  },
  {
    q: '¿Puedo enviarle notas de voz?',
    a: 'Sí. Secretario transcribe la nota de voz y la atiende como si la hubieras escrito.',
  },
  {
    q: '¿Funciona con cuentas de Google Workspace?',
    a: 'Sí, siempre que el administrador de tu organización permita conectar aplicaciones de terceros.',
  },
  {
    q: '¿Cómo dejo de usarlo?',
    a: 'Escríbele «desconecta mi cuenta de Google» para quitarle el acceso a Google y pídenos por WhatsApp o por correo que demos de baja tu número. Al darte de baja borramos tu registro, tus autorizaciones y tus avisos programados.',
  },
  {
    q: '¿Cuánto cuesta?',
    a: 'Secretario está en acceso anticipado. Escríbenos y te contamos las condiciones.',
  },
]

const Secretario = () => (
  <Layout>
    <Helmet>
      <title>Secretario · Tu asistente personal en WhatsApp | Variant</title>
      <meta
        name="description"
        content="Secretario es un asistente de IA en WhatsApp que agenda reuniones, envía correos, crea tareas y te recuerda lo importante con tu cuenta de Google, siempre con tu confirmación."
      />
      <link rel="canonical" href={`${SITE.url}/secretario`} />
      <meta property="og:title" content="Secretario · Tu asistente personal en WhatsApp" />
      <meta property="og:url" content={`${SITE.url}/secretario`} />
    </Helmet>

    <section className="s-hero">
      <div className="container s-hero__grid">
        <div>
          <span className="badge">Acceso anticipado</span>
          <h1 className="s-hero__title">
            Secretario, tu asistente personal en <span>WhatsApp</span>
          </h1>
          <p className="s-hero__lead">
            Pídele por mensaje o nota de voz que agende reuniones, envíe correos, cree tareas y te recuerde lo
            importante. Secretario lo resuelve con tu cuenta de Google y te pide confirmación antes de actuar.
          </p>
          <div className="btn-row">
            <a href={WA_SECRETARIO} className="btn btn--primary" target="_blank" rel="noopener noreferrer">
              <Icon name="chat" size={18} /> Solicitar acceso
            </a>
            <a href="#permisos" className="btn btn--ghost">
              Permisos y privacidad
            </a>
          </div>
          <p className="s-hero__by">
            Un producto de <Link to="/">Variant</Link>.
          </p>
        </div>
        <ChatMockup messages={HERO_CHAT} />
      </div>
    </section>

    <section className="section section--soft" id="funciones">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Qué puede hacer</span>
          <h2 className="section-title">Lo que harías en cinco aplicaciones, en un solo chat</h2>
          <p className="section-lead">
            Secretario entiende lo que le pides en lenguaje natural y usa tus herramientas de Google para hacerlo.
          </p>
        </div>
        <div className="grid-3">
          {CAPABILITIES.map((c) => (
            <div key={c.title} className="card capability">
              <span className="icon-chip">
                <Icon name={c.icon} size={22} />
              </span>
              <h3>{c.title}</h3>
              <p>{c.text}</p>
              <p className="capability__example">{c.example}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section" id="como-funciona">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Cómo funciona</span>
          <h2 className="section-title">Empieza en minutos</h2>
        </div>
        <ol className="steps">
          {STEPS.map((s, i) => (
            <li key={s.title} className="step">
              <span className="step__num">{i + 1}</span>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="section section--soft" id="permisos">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Permisos de Google</span>
          <h2 className="section-title">Qué datos de Google usa Secretario y para qué</h2>
          <p className="section-lead">
            Secretario solo te pide los permisos de las herramientas que vayas a usar, y los usa únicamente para
            cumplir lo que le pides.
          </p>
        </div>
        <div className="scopes">
          <div className="scopes__row scopes__row--head" aria-hidden="true">
            <span>Producto y permiso</span>
            <span>Para qué lo usa Secretario</span>
          </div>
          {SCOPES.map((s) => (
            <div key={s.product} className="scopes__row">
              <div>
                <strong>{s.product}</strong>
                <span className="scopes__access">{s.access}</span>
                <code>{s.scope}</code>
              </div>
              <p>{s.use}</p>
            </div>
          ))}
        </div>
        <div className="limited-use">
          <Icon name="shield" size={22} />
          <div>
            <p>
              El uso y la transferencia a cualquier otra aplicación de la información recibida de las API de Google
              por parte de Secretario se ajustan a la{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                Política de datos de usuario de los servicios de las API de Google
              </a>
              , incluidos los requisitos de Uso Limitado.
            </p>
            <p className="limited-use__en" lang="en">
              Secretario's use and transfer to any other app of information received from Google APIs will adhere to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>
            <p>
              No vendemos tus datos, no los usamos para publicidad ni para entrenar modelos de IA. Los detalles están en
              la <Link to="/politica-de-privacidad">política de privacidad</Link>.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="section" id="control">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Tu control</span>
          <h2 className="section-title">Tú decides qué hace y qué recuerda</h2>
        </div>
        <div className="controls">
          {CONTROLS.map((c) => (
            <div key={c.title} className="card">
              <span className="icon-chip">
                <Icon name={c.icon} size={22} />
              </span>
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
        <p className="controls__note">
          Puedes revisar o quitar el acceso de Secretario en cualquier momento desde{' '}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
            myaccount.google.com/permissions
          </a>
          .
        </p>
      </div>
    </section>

    <section className="section section--soft" id="preguntas">
      <div className="container">
        <div className="section-head section-head--center">
          <span className="eyebrow">Preguntas frecuentes</span>
          <h2 className="section-title">Lo que suelen preguntarnos</h2>
        </div>
        <div className="faq">
          {FAQ.map((f) => (
            <details key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container">
        <div className="cta-band">
          <h2>Delega lo repetitivo a Secretario</h2>
          <p>Estamos abriendo cupos de acceso anticipado. Escríbenos y te registramos.</p>
          <div className="btn-row">
            <a href={WA_SECRETARIO} className="btn btn--primary" target="_blank" rel="noopener noreferrer">
              <Icon name="chat" size={18} /> Solicitar acceso por WhatsApp
            </a>
            <a href={`mailto:${SITE.email}?subject=Acceso%20a%20Secretario`} className="btn btn--ghost">
              <Icon name="mail" size={18} /> Escribir un correo
            </a>
          </div>
        </div>
      </div>
    </section>
  </Layout>
)

export default Secretario
